import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { AlertTriangle, TrendingUp, Package, Wrench, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  green:  { color: "bg-green-100 text-green-800 border-green-300",  emoji: "🟢", label: "Dentro da meta" },
  yellow: { color: "bg-yellow-100 text-yellow-800 border-yellow-300", emoji: "🟡", label: "Atenção" },
  red:    { color: "bg-red-100 text-red-800 border-red-300",        emoji: "🔴", label: "Crítico" },
  na:     { color: "bg-slate-100 text-slate-500 border-slate-300",  emoji: "⚪", label: "Sem dados" },
};

const GROUP_LABELS = {
  admin: "📋 Administrativo",
  sandblasting: "⚙️ Jateamento",
  painting: "🎨 Pintura",
  operations: "🔧 Operações",
  materials: "📦 Consumo de Materiais",
  quality: "⚠️ Qualidade",
  // Fase C — perguntas customizadas (target_section)
  morning: "🌅 Manhã (perguntas customizadas)",
  lunch: "🍽️ Almoço (perguntas customizadas)",
  afternoon: "🌇 Tarde (perguntas customizadas)",
  photos: "📸 Fotos (perguntas customizadas)",
  activities: "📊 Atividades (perguntas customizadas)",
  impediments: "⚠️ Impedimentos (perguntas customizadas)",
  warehouse: "📦 Almoxarifado (perguntas customizadas)",
  closing: "✅ Encerramento (perguntas customizadas)",
  demands: "📝 Demandas (perguntas customizadas)",
};

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

const formatNum = (v, digits = 1) => {
  if (v === null || v === undefined) return "—";
  if (typeof v !== "number") return v;
  return v.toLocaleString("pt-BR", { maximumFractionDigits: digits });
};

const AnalyticsDashboard = ({ client, open }) => {
  // Padrão: "Todo o período" (campos vazios) para o usuário ver toda a base ao abrir
  const [dateRange, setDateRange] = useState({ start_date: "", end_date: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (days) => {
    if (days === null) {
      setDateRange({ start_date: "", end_date: "" });
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - days * 86400000).toISOString().split("T")[0];
    setDateRange({ start_date: from, end_date: today });
  };

  const load = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      // Enviar apenas params preenchidos — permite backend usar "todo período"
      const params = {};
      if (dateRange.start_date) params.start_date = dateRange.start_date;
      if (dateRange.end_date) params.end_date = dateRange.end_date;
      const res = await axios.get(`${API}/analytics/client/${client.id}`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar análise");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client?.id, dateRange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!client) return null;

  const healthScore = data?.health_score;
  const healthColor =
    healthScore === null || healthScore === undefined ? "text-slate-500" :
    healthScore >= 80 ? "text-green-600" :
    healthScore >= 50 ? "text-yellow-600" : "text-red-600";

  // Agrupar comparações
  const byGroup = {};
  (data?.comparisons || []).forEach(c => {
    byGroup[c.group] = byGroup[c.group] || [];
    byGroup[c.group].push(c);
  });

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Filtros de período */}
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-3 rounded-lg">
        <div>
          <Label className="text-xs">Data Início</Label>
          <Input
            type="date"
            data-testid="analytics-start-date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Data Fim</Label>
          <Input
            type="date"
            data-testid="analytics-end-date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
          />
        </div>
        <Button onClick={load} disabled={loading} data-testid="analytics-refresh-btn" className="bg-blue-600 hover:bg-blue-700">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Carregando..." : "Atualizar"}
        </Button>
        <div className="flex gap-1.5 items-center">
          <span className="text-xs text-slate-500 mr-1">Atalhos:</span>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="preset-30d" onClick={() => applyPreset(30)}>30d</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="preset-90d" onClick={() => applyPreset(90)}>90d</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="preset-365d" onClick={() => applyPreset(365)}>1 ano</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="preset-all" onClick={() => applyPreset(null)}>Todo período</Button>
        </div>
        <div className="ml-auto text-sm text-slate-600">
          <span className="font-medium">Tolerância:</span> ±{data?.client?.deviation_tolerance_percentage ?? 10}%
        </div>
      </div>

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-health-score">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-slate-600 font-medium">🎯 Saúde do Contrato</CardTitle></CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${healthColor}`}>{healthScore ?? "—"}</div>
            <div className="text-xs text-slate-500">de 100 pts</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-total-reports">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-slate-600 font-medium">📊 RDOs no período</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{data?.total_reports ?? "—"}</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-impediments">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-slate-600 font-medium">⚠️ Tipos de Impedimento</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{data?.impediment_breakdown?.length ?? 0}</div>
            <div className="text-xs text-slate-500">
              {(data?.impediment_breakdown || []).reduce((s, b) => s + b.count, 0)} ocorrências
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pending-materials">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-slate-600 font-medium">📦 Materiais pendentes</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{data?.warehouse?.pending_registration?.length ?? 0}</div>
            <div className="text-xs text-slate-500">de cadastro</div>
          </CardContent>
        </Card>
      </div>

      {loading && !data && (
        <div className="text-center py-10 text-slate-500">🔄 Carregando análise...</div>
      )}

      {data && data.total_reports === 0 && (
        <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-lg text-slate-600">
          Nenhum RDO encontrado para este cliente no período selecionado.
        </div>
      )}

      {data && data.total_reports > 0 && (
        <>
          {/* Tabela comparativa por grupo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Padrão vs Realizado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {Object.keys(byGroup).map(group => (
                <div key={group} data-testid={`comparison-group-${group}`}>
                  <h4 className="font-semibold text-sm mb-2 text-slate-700">{GROUP_LABELS[group] || group}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-slate-600">
                          <th className="py-2">Métrica</th>
                          <th className="py-2">Padrão</th>
                          <th className="py-2">Realizado</th>
                          <th className="py-2">Variação</th>
                          <th className="py-2">Status</th>
                          <th className="py-2 text-right">Amostras</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byGroup[group].map(c => {
                          const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.na;
                          return (
                            <tr key={c.key} className="border-b hover:bg-slate-50" data-testid={`comparison-row-${c.key}`}>
                              <td className="py-2">{c.label}</td>
                              <td className="py-2 font-mono">{formatNum(c.standard)} {c.unit}</td>
                              <td className="py-2 font-mono">{formatNum(c.actual)} {c.unit}</td>
                              <td className="py-2 font-mono">
                                {c.variance_pct !== null && c.variance_pct !== undefined
                                  ? `${c.variance_pct > 0 ? "+" : ""}${c.variance_pct}%`
                                  : "—"}
                              </td>
                              <td className="py-2">
                                <Badge className={cfg.color + " border"}>{cfg.emoji} {cfg.label}</Badge>
                              </td>
                              <td className="py-2 text-right text-slate-500">{c.samples}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Séries temporais */}
          {(data.time_series?.sandblasting_hours?.length > 0 ||
            data.time_series?.painting_hours?.length > 0) && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Evolução de Produtividade</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={mergeTimeSeries([
                    { key: "sandblasting", label: "Jateamento (h)", data: data.time_series.sandblasting_hours || [] },
                    { key: "painting", label: "Pintura (h)", data: data.time_series.painting_hours || [] },
                  ])}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Jateamento (h)" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="Pintura (h)" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {data.time_series?.impediment_minutes?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">⏱️ Tempo em Impedimentos por Dia</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.time_series.impediment_minutes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip formatter={(v) => `${v} min`} />
                      <Bar dataKey="value" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">📊 Distribuição por Tipo</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={data.impediment_breakdown}
                        dataKey="count"
                        nameKey="label"
                        cx="50%" cy="50%"
                        outerRadius={90}
                        label={(e) => `${e.label} (${e.count})`}
                        labelLine={false}
                      >
                        {data.impediment_breakdown.map((entry, i) => (
                          <Cell key={entry.label} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Almoxarifado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Itens cadastrados */}
            {data.warehouse?.catalog?.length > 0 && (
              <Card data-testid="warehouse-catalog-card">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> Itens de Almoxarifado</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-slate-600">
                          <th className="py-2">Item</th>
                          <th className="py-2 text-right">Mín.</th>
                          <th className="py-2 text-right">Mencionado</th>
                          <th className="py-2">Destino</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.warehouse.catalog.map(it => (
                          <tr key={it.id} className="border-b">
                            <td className="py-2 font-medium">
                              {it.name}
                              {it.unit && <span className="text-xs text-slate-500 ml-1">({it.unit})</span>}
                            </td>
                            <td className="py-2 text-right font-mono">{formatNum(it.min_stock)}</td>
                            <td className="py-2 text-right font-mono">
                              {it.times_mentioned > 0 ? (
                                <Badge variant="secondary">{it.times_mentioned}x</Badge>
                              ) : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="py-2 text-xs capitalize">{(it.standard_destination || "—").replace("_", " ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pendentes de cadastro */}
            <Card data-testid="pending-registration-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" /> Materiais Pendentes de Cadastro
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.warehouse?.pending_registration?.length > 0 ? (
                  <div className="space-y-2">
                    {data.warehouse.pending_registration.map((m) => (
                      <div key={m.name} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-2 text-sm">
                        <span>{m.name}</span>
                        <Badge variant="outline" className="text-amber-700 border-amber-400">
                          {m.times}x em RDOs
                        </Badge>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-2">
                      Cadastre esses itens no Cliente para aparecerem na lista do RDO e no controle de estoque.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    ✅ Todos os materiais dos RDOs estão cadastrados.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Reposições urgentes + Kit turbina */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.warehouse?.urgent_replacements?.length > 0 && (
              <Card data-testid="urgent-replacements-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" /> Reposições com Urgência
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.warehouse.urgent_replacements.map((u) => (
                      <div key={`${u.material}-${u.date}`} className="bg-red-50 border border-red-200 rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{u.material}</span>
                          <span className="text-xs text-slate-500">{u.date}</span>
                        </div>
                        {u.justification && <div className="text-xs text-slate-600 mt-1">↳ {u.justification}</div>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card data-testid="kit-turbine-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wrench className="w-4 h-4" /> Kit Turbina
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.warehouse?.kit_turbine?.alert && (
                  <div className={`mb-3 p-2 rounded border text-sm ${
                    data.warehouse.kit_turbine.alert.status === "red" ? "bg-red-50 border-red-300" :
                    data.warehouse.kit_turbine.alert.status === "yellow" ? "bg-yellow-50 border-yellow-300" :
                    "bg-green-50 border-green-300"
                  }`}>
                    <strong>{data.warehouse.kit_turbine.alert.days_since} dias</strong> desde a última troca
                    (padrão: {data.warehouse.kit_turbine.alert.standard_days} dias)
                  </div>
                )}
                {data.warehouse?.kit_turbine?.history?.length > 0 ? (
                  <div className="space-y-1 text-sm">
                    {data.warehouse.kit_turbine.history.map((h) => (
                      <div key={`${h.date}-${h.position || ''}`} className="flex justify-between border-b pb-1">
                        <span>{h.date} — <span className="capitalize">{h.position || "—"}</span></span>
                        <span className="text-xs text-slate-500">{h.balanced ? "✓ balanceado" : ""}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Nenhuma troca de Kit Turbina registrada no período.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// Mescla séries diferentes por data para o LineChart multi-linha
const mergeTimeSeries = (series) => {
  const map = {};
  series.forEach(({ label, data }) => {
    (data || []).forEach(pt => {
      map[pt.date] = map[pt.date] || { date: pt.date };
      map[pt.date][label] = pt.value;
    });
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

export default AnalyticsDashboard;
